import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import type { OuNode } from './ouTree';

interface OuTreeRowProps {
  node: OuNode;
  depth: number;
  isCollapsed: (id: string) => boolean;
  onToggle: (id: string) => void;
  renderCells: (ou: OUItem) => ReactNode;
}

export function OuTreeRow({ node, depth, isCollapsed, onToggle, renderCells }: OuTreeRowProps) {
  const { t } = useTranslation();
  const { ou, children } = node;
  const hasChildren = children.length > 0;
  const collapsed = hasChildren && isCollapsed(ou.id);

  return (
    <>
      <tr className={ou.isActive ? '' : 'ou-row--inactive'}>
        <td className="ou-cell-name">
          <span className="ou-name" style={{ paddingLeft: depth * 20 }}>
            {hasChildren ? (
              <button
                type="button"
                className={`ou-chevron-btn${collapsed ? '' : ' ou-chevron-btn--open'}`}
                onClick={() => onToggle(ou.id)}
                aria-expanded={!collapsed}
                aria-label={collapsed ? t('admin.ouExpand') : t('admin.ouCollapse')}
              >
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            ) : depth > 0 ? (
              <i className="fas fa-turn-up fa-rotate-90 ou-name-branch" aria-hidden="true" />
            ) : null}
            {ou.name}
          </span>
          {ou.id && <span className="ou-id">{ou.id}</span>}
          {ou.azureAdGroupId && (
            <span className="ou-id" title={ou.azureAdGroupId}>
              <i className="fas fa-link" /> AAD
            </span>
          )}
        </td>
        {renderCells(ou)}
      </tr>
      {hasChildren && !collapsed && children.map(child => (
        <OuTreeRow
          key={child.ou.id}
          node={child}
          depth={depth + 1}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          renderCells={renderCells}
        />
      ))}
    </>
  );
}
